import * as z from 'zod';
import { PaymentMethod } from '../../../offer-checkout/constants/payment-method.enum';
import { isValidPhone } from '../../../offer-checkout/lib/phone';
import { BillingTerm } from '../../../products/constants/billing-term.enum';
import { centsToEuroInput, parseEuroInputToCents } from '../../../products/lib/money';
import { CreateOfferRequestDto } from '../../types/request';
import { OfferFormOptionsResponseDto, OfferMetaResponseDto, OfferResponseDto } from '../../types/response';

const optionalMailSchema = z
  .string()
  .trim()
  .refine((value) => !value || z.string().email().safeParse(value).success, 'Bitte eine gültige E-Mail-Adresse eingeben');

const customerSchema = z.object({
  mail: z.string().trim().min(1, 'E-Mail ist erforderlich').email('Bitte eine gültige E-Mail-Adresse eingeben'),
  firstName: z.string(),
  lastName: z.string(),
  name: z.string(),
  vatId: z.string(),
  street: z.string(),
  houseNumber: z.string(),
  postCode: z.string(),
  city: z.string(),
  country: z.string(),
  invoiceMail: optionalMailSchema,
  phone: z.string().refine((value) => !value.trim() || isValidPhone(value), 'Bitte eine gültige Telefonnummer eingeben, z. B. +491771234567'),
  newCustomerDate: z.string(),
  // Only the correction form edits them – an offer is never prefilled with bank data.
  iban: z.string(),
  bic: z.string()
});

/** Mirrors `CreateOfferRequestDto` – title, description, price and conditions with the same limits as the product form. */
export const offerFormSchema = z.object({
  term: z.nativeEnum(BillingTerm, { errorMap: () => ({ message: 'Bitte eine Laufzeit wählen' }) }),
  productId: z.string().min(1, 'Bitte ein Produkt wählen'),
  title: z.string().trim().min(1, 'Titel ist erforderlich').max(120, 'Maximal 120 Zeichen'),
  description: z.string().max(2000, 'Maximal 2000 Zeichen'),
  monthlyPrice: z.string().refine((value) => parseEuroInputToCents(value) !== null, 'Bitte einen gültigen Betrag eingeben, z. B. 499,00'),
  additionalConditions: z.string().max(300, 'Maximal 300 Zeichen'),
  addOnIds: z.array(z.string()),
  paymentMethod: z.nativeEnum(PaymentMethod, { errorMap: () => ({ message: 'Bitte eine Zahlungsart wählen' }) }),
  customer: customerSchema
});

export type OfferFormValues = z.infer<typeof offerFormSchema>;

export type OfferCustomerFieldName = keyof OfferFormValues['customer'];

export const OFFER_FORM_DEFAULTS: Partial<OfferFormValues> = {
  productId: '',
  title: '',
  description: '',
  monthlyPrice: '',
  additionalConditions: '',
  addOnIds: [],
  customer: {
    mail: '',
    firstName: '',
    lastName: '',
    name: '',
    vatId: '',
    street: '',
    houseNumber: '',
    postCode: '',
    city: '',
    country: '',
    invoiceMail: '',
    phone: '',
    newCustomerDate: '',
    iban: '',
    bic: ''
  }
};

/** Empty optional customer fields are left out of the request instead of being sent as "". */
const optional = (value: string): string | undefined => value.trim() || undefined;

export const toCreateOfferRequest = (values: OfferFormValues): CreateOfferRequestDto => {
  const { customer } = values;

  return {
    term: values.term,
    productId: values.productId,
    title: values.title.trim(),
    description: values.description.trim(),
    monthlyPriceCents: parseEuroInputToCents(values.monthlyPrice) ?? 0,
    additionalConditions: values.additionalConditions.trim() || null,
    addOnIds: values.addOnIds,
    paymentMethod: values.paymentMethod,
    customer: {
      mail: customer.mail.trim().toLowerCase(),
      firstName: optional(customer.firstName),
      lastName: optional(customer.lastName),
      name: optional(customer.name),
      vatId: optional(customer.vatId),
      street: optional(customer.street),
      houseNumber: optional(customer.houseNumber),
      postCode: optional(customer.postCode),
      city: optional(customer.city),
      country: optional(customer.country),
      invoiceMail: optional(customer.invoiceMail),
      phone: optional(customer.phone),
      newCustomerDate: optional(customer.newCustomerDate)
    }
  };
};

/**
 * A saved draft as form values. Add-ons archived since and a start date no longer selectable are left out – the form shows
 * what can still be saved. An archived product stays selected: saving then names the problem.
 */
export const toOfferFormValues = (offer: OfferResponseDto, options: OfferFormOptionsResponseDto, meta: OfferMetaResponseDto): OfferFormValues => {
  const { product, customer } = offer;
  const activeAddOnIds = options.addOns.map(({ _id }) => _id);

  return {
    term: product.term,
    productId: product.productId,
    title: product.title,
    description: product.description,
    monthlyPrice: centsToEuroInput(product.monthlyPriceCents),
    additionalConditions: product.additionalConditions ?? '',
    addOnIds: offer.addOns.map(({ addOnId }) => addOnId).filter((id) => activeAddOnIds.includes(id)),
    paymentMethod: offer.paymentMethod,
    customer: {
      mail: customer.mail,
      firstName: customer.firstName ?? '',
      lastName: customer.lastName ?? '',
      name: customer.name ?? '',
      vatId: customer.vatId ?? '',
      street: customer.street ?? '',
      houseNumber: customer.houseNumber ?? '',
      postCode: customer.postCode ?? '',
      city: customer.city ?? '',
      country: customer.country ?? '',
      invoiceMail: customer.invoiceMail ?? '',
      phone: customer.phone ?? '',
      newCustomerDate: customer.newCustomerDate && meta.startDateOptions.includes(customer.newCustomerDate) ? customer.newCustomerDate : '',
      iban: '',
      bic: ''
    }
  };
};
